import { AccountModel, AccountQueryOptions, AccountStatus, AccountSummaryModel, AccountType, IAccountData } from '@truckdown/accounts';
import context from '@truckdown/components-systems';
import { showMessage } from "../Utilities/Utilities";
import { defineComponent, ref, Ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

const resultsPerPage = 10;

interface IData {
    isLoading: Ref<boolean>,
    members: Ref<AccountModel[]>;
    membersCount: Ref<number>,
    accountId: Ref<string>;

    pageCount: Ref<number>,
    currentQuery: Ref<AccountQueryOptions>;
    resetList: Ref<boolean>,

    showMemberDropdown: Ref<number>
}

interface IPageKey {
    lastNumber?: number
}

const getInitialData = function (props: any) {
    let query = {
        limit: resultsPerPage
    };

    return {
        isLoading: ref<boolean>(false),
        members: ref<AccountModel[]>(props?.members || []),
        membersCount: ref<number>(props.count || 0),
        accountId: ref<string>(props.accountId || ""),
        
        pageCount: ref<number>(resultsPerPage),
        currentQuery: ref<AccountQueryOptions>(query),
        resetList: ref<boolean>(false),

        showMemberDropdown: ref<number>(-1),    
    } as IData;
};

const getComputed = function (data: IData) {
    return {
        lastPagerObject: computed(() => {
            if (data.members.value && data.members.value.length > 0) {
                let last = (data.members.value)[data.members.value.length - 1];
                return {
                    lastNumber: last.ain
                }
            }
        })
    };
};

const getMethods = function (data: IData, props: any) {
    return {
        updateList: async function (updateArgs?: IPageKey) {
            data.showMemberDropdown.value = -1;
            await loadMembers(data, data.currentQuery.value, updateArgs);
        },
        getStatus: function (status: AccountStatus) {
            return AccountStatus[status];
        },
        getType: function (type: AccountType) {
            return AccountType[type];
        },
        getAddress: function (account: AccountModel) {
            if (account.info?.address?.city) {
                return account.info.address.city + ", " + account.info.address.regionCode;
            }
            return "";
        },
        toggleMemberDropdown: function (index: number) {
            if (data.showMemberDropdown.value == index) {
                data.showMemberDropdown.value = -1;
            } else {
                data.showMemberDropdown.value = index;
            }
        },
        dateDisplay: function (dateUTC: Date | string): Date | string | undefined {
            if (dateUTC) {
                return new Date(dateUTC).toLocaleString();
            } else
                return undefined;
        },
        goToAccount: function (account: AccountModel) {
            window.location.href = `/accounts/admin/${encodeURIComponent(account.ain)}`;
        },
        formatPhoneString: function (phone: string) {
            if (phone?.length == 10) {
                phone = phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
            }
            return phone;
        }
    };
}

const loadMembers = async function (data: IData, query: AccountQueryOptions, page?: IPageKey) {
    if (!data.isLoading.value && data.accountId.value) {
        data.isLoading.value = true;
        try {

            if (page) {
                query.lastNumber = page.lastNumber;
            }

            const accountData = await context.getService<IAccountData>('IAccountData');
            const result = await accountData.getMembers(data.accountId.value, query);

            if (result.ok && result.data) {
                query.lastNumber = undefined;
                data.currentQuery.value = query;

                data.members.value = result.data;
                await setCount(data, query);
            }
            if (!result.ok) {
                showMessage('An error occured while querying member accounts.', "danger", true, true);
            }

        } catch (err) {
            console.error("Error querying member accounts.");
            showMessage('An error occured while querying member accounts.', "danger", true, true);
        } finally {
            data.isLoading.value = false;
        }
    }
};

const setCount = async function (data: IData, options: AccountQueryOptions) {
    try {
        const accountData = await context.getService<IAccountData>('IAccountData');
        const result = await accountData.getMembersCount(data.accountId.value);
        data.membersCount.value = result.data ?? 0;
    } catch (err) {
        console.error("Error getting member accounts count.");
    }
}

export default defineComponent({
    name: "Members",
    props: {
        accountId: String,
        members: Array as () => AccountModel[],
        count: Number
    },
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let methods = getMethods(data, props);
        let computed = getComputed(data);

        return {
            ...data,
            ...methods,
            ...computed
        }
    }
})